import React from "react"
import { Link } from "gatsby"

import { computeTopicStats } from "./topicStats"
import { sortCommunities } from "./communities"
import { readableNumber } from "./text"

const SAMPLE_SMALL = 30
const SAMPLE_VERY_SMALL = 15
const TOP_COMMUNITIES = 4
const communityLinkClass = "font-semibold underline"

function directoryPhrase(siteName) {
  return `the ${siteName} directory of communities`
}

function joinNames(items) {
  if (!items.length) return ""
  if (items.length === 1) return items[0]
  if (items.length === 2) return `${items[0]} and ${items[1]}`
  return `${items.slice(0, -1).join(", ")}, and ${items[items.length - 1]}`
}

function sampleSizeNote(total) {
  if (!total || total >= SAMPLE_SMALL) return ""
  if (total < SAMPLE_VERY_SMALL) {
    return " We only have a handful of listings so far, so treat these numbers as directional."
  }
  return " This is a smaller slice of our database, so read trends as directional."
}

function toMembers(c) {
  const n = Number(c.members)
  return Number.isFinite(n) && n > 0 ? n : null
}

function isFreeAccess(c) {
  return !c.paid && !c.apply
}

function communityPlainSuffix(c) {
  const members = toMembers(c)
  if (members == null) return ""
  return ` (${readableNumber(members)} members)`
}

function joinPlainCommunities(communities) {
  return joinNames(
    communities.map(c => `${c.name}${communityPlainSuffix(c)}`)
  )
}

function joinPlainCommunityNames(communities) {
  return joinNames(communities.map(c => c.name))
}

function joinLinkedCommunities(communities) {
  if (!communities.length) return null
  if (communities.length === 1) {
    return (
      <Link to={`/communities/${communities[0].slug}/`} className={communityLinkClass}>
        {communities[0].name}
      </Link>
    )
  }
  if (communities.length === 2) {
    return (
      <>
        <Link to={`/communities/${communities[0].slug}/`} className={communityLinkClass}>
          {communities[0].name}
        </Link>
        {" and "}
        <Link to={`/communities/${communities[1].slug}/`} className={communityLinkClass}>
          {communities[1].name}
        </Link>
      </>
    )
  }
  return communities.map((c, index) => (
    <React.Fragment key={c.slug}>
      {index > 0 && (index === communities.length - 1 ? ", and " : ", ")}
      <Link to={`/communities/${c.slug}/`} className={communityLinkClass}>
        {c.name}
      </Link>
    </React.Fragment>
  ))
}

function joinLinkedPlatforms(platforms, topicSlug) {
  if (!platforms.length) return null
  if (platforms.length === 1) {
    const p = platforms[0]
    return (
      <>
        <Link
          to={`/topics/${topicSlug}/platform/${p.slug}/`}
          className={communityLinkClass}
        >
          {p.name}
        </Link>
        {` (${p.count})`}
      </>
    )
  }
  return platforms.map((p, index) => (
    <React.Fragment key={p.slug}>
      {index > 0 && (index === platforms.length - 1 ? ", and " : ", ")}
      <Link
        to={`/topics/${topicSlug}/platform/${p.slug}/`}
        className={communityLinkClass}
      >
        {p.name}
      </Link>
      {` (${p.count})`}
    </React.Fragment>
  ))
}

function memberCountCopy(stats, communities) {
  const withMembers = communities.filter(c => toMembers(c) != null)
  if (!withMembers.length) return ""

  const largest = sortCommunities("Largest", [...withMembers]).slice(0, 3)
  const parts = largest.map(
    c => `${c.name} (${readableNumber(toMembers(c))} members)`
  )
  let copy = ` The largest include ${joinNames(parts)}.`
  if (stats.medianMembers != null) {
    copy += ` Median reported size is about ${readableNumber(
      Math.round(stats.medianMembers)
    )} members (${withMembers.length} listings with member data).`
  }
  return copy
}

function featureTopicPath(topicSlug, featureSlug) {
  return `/topics/${topicSlug}/feature/${featureSlug}/`
}

function linkedFeatureName(feature, topicSlug, templateFeatureSlugs) {
  if (templateFeatureSlugs?.includes(feature.slug)) {
    return (
      <Link
        to={featureTopicPath(topicSlug, feature.slug)}
        className="font-semibold underline"
      >
        {feature.name}
      </Link>
    )
  }
  return feature.name
}

function joinLinkedFeatureNames(features, topicSlug, templateFeatureSlugs) {
  if (!features.length) return null
  if (features.length === 1) {
    return linkedFeatureName(features[0], topicSlug, templateFeatureSlugs)
  }
  if (features.length === 2) {
    return (
      <>
        {linkedFeatureName(features[0], topicSlug, templateFeatureSlugs)}
        {" and "}
        {linkedFeatureName(features[1], topicSlug, templateFeatureSlugs)}
      </>
    )
  }
  return features.map((feature, index) => (
    <React.Fragment key={feature.slug}>
      {index > 0 && (index === features.length - 1 ? ", and " : ", ")}
      {linkedFeatureName(feature, topicSlug, templateFeatureSlugs)}
    </React.Fragment>
  ))
}

function buildTopicPageFAQ({
  topic,
  communities,
  platforms,
  features,
  updatedDate,
  platform = null,
  siteName = "Hive Index",
  templateFeatureSlugs = [],
}) {
  const stats = computeTopicStats({
    topic,
    communities,
    platforms,
    features,
    reviews: [],
  })

  const total = stats.total
  if (!total) return []

  const topicName = topic.name
  const lower = topicName.toLowerCase()
  const isPlatformPage = Boolean(platform)
  const platformPlural =
    platform?.groupNamePlural || platform?.name || "communities"
  const platformLabel = platformPlural.toLowerCase()
  const platformName = platform?.name || ""
  const hasPromoted = communities.some(c => c.promoted)
  const sampleNote = sampleSizeNote(total)
  const description = topic.description?.trim()
  const topicIntro = description
    ? `${description}${/[.!?]$/.test(description) ? "" : "."}`
    : `Online groups where people connect around ${lower}-related interests, often through chat, forums, events, or membership platforms.`
  const subject = isPlatformPage
    ? `${lower} ${platformLabel}`
    : `${lower} communities`
  const directory = directoryPhrase(siteName)

  const topPopularCommunities = sortCommunities("Popular", [...communities]).slice(
    0,
    TOP_COMMUNITIES
  )
  const topRated = stats.highestRatedCommunities.slice(0, TOP_COMMUNITIES)
  const topFreeCommunities = sortCommunities(
    "Popular",
    communities.filter(isFreeAccess)
  ).slice(0, TOP_COMMUNITIES)
  const topPaidCommunities = sortCommunities(
    "Popular",
    communities.filter(c => c.paid)
  ).slice(0, TOP_COMMUNITIES)

  const faqs = []

  if (isPlatformPage) {
    faqs.push({
      question: `What are ${topicName} ${platformLabel}?`,
      answer: `${topicIntro} This page focuses on communities hosted on ${platformName}, so you can compare their size, features, reviews, and access requirements in one place.`,
    })
  } else {
    faqs.push({
      question: `What are ${lower} communities?`,
      answer: topicIntro,
    })
  }

  if (topRated.length >= 2) {
    const bestPlain = `${joinPlainCommunityNames(
      topRated
    )} have the highest review scores in ${directory}. Reviews come from ${siteName} members and reflect their experiences, not an editorial ranking. See more rated listings in ${directory} on ${siteName}.`
    faqs.push({
      question: `What are the best ${subject} to join?`,
      answerPlain: bestPlain,
      answer: (
        <>
          {joinLinkedCommunities(topRated)} have the highest review scores in{" "}
          {directory}. Reviews come from {siteName} members and reflect their
          experiences, not an editorial ranking. See more rated listings in{" "}
          {directory} on {siteName}.
        </>
      ),
    })
  } else if (topPopularCommunities.length) {
    const popularPlain = `${joinPlainCommunityNames(
      topPopularCommunities
    )} are among the most popular ${subject} in ${directory}, based on visitor interest on ${siteName}. This is not a universal quality score. You can also compare listings by review scores, member counts, and listing age on ${siteName}.`
    faqs.push({
      question: `What are the most popular ${subject} on ${siteName}?`,
      answerPlain: popularPlain,
      answer: (
        <>
          {joinLinkedCommunities(topPopularCommunities)} are among the most
          popular {subject} in {directory}, based on visitor interest on{" "}
          {siteName}. This is not a universal quality score. You can also
          compare listings by review scores, member counts, and listing age on{" "}
          {siteName}.
        </>
      ),
    })
  }

  if (!isPlatformPage) {
    const topPlatforms = stats.platformBreakdown.slice(0, 3)
    if (topPlatforms.length) {
      const platformPlain = `${joinNames(
        topPlatforms.map(p => `${p.name} (${p.count})`)
      )} ${
        topPlatforms.length === 1 ? "is" : "are"
      } the most common platforms for ${lower} communities in ${directory}.`
      faqs.push({
        question: `Which platforms host the most ${lower} communities?`,
        answerPlain: platformPlain,
        answer: (
          <>
            {joinLinkedPlatforms(topPlatforms, topic.slug)}{" "}
            {topPlatforms.length === 1 ? "is" : "are"} the most common platforms
            for {lower} communities in {directory}.
          </>
        ),
      })
    }
  }

  const free = stats.accessBreakdown.find(item => item.key === "free")
  const restricted = stats.accessBreakdown.find(item => item.key === "restricted")
  if (free) {
    if (free.percent === 100) {
      const freePlain = topFreeCommunities.length
        ? `All ${total} listings in ${directory} are marked as free to join. Popular examples include ${joinPlainCommunityNames(
            topFreeCommunities
          )}. Access rules can still change on the community side.`
        : `All ${total} listings in ${directory} are marked as free to join, with no paid membership or application gate listed. Access rules can still change on the community side.`
      faqs.push({
        question: `Are ${subject} free to join?`,
        answerPlain: freePlain,
        answer: topFreeCommunities.length ? (
          <>
            All {total} listings in {directory} are marked as free to join.
            Popular examples include {joinLinkedCommunities(topFreeCommunities)}
            . Access rules can still change on the community side.
          </>
        ) : (
          freePlain
        ),
      })
    } else if (free.count === 0) {
      faqs.push({
        question: `Are there free ${subject}?`,
        answer: `Every ${subject} listing in ${directory} requires payment, an application, or both.${
          isPlatformPage
            ? ` See the main ${topicName} topic page to compare access across all platforms.`
            : ""
        }`,
      })
    } else {
      const freePlain = topFreeCommunities.length
        ? `${free.count} of ${total} (${free.percent}%) are marked as free to join, including ${joinPlainCommunityNames(
            topFreeCommunities
          )}. The rest require payment, an application, or both.`
        : `${free.count} of ${total} (${free.percent}%) are marked as free to join. The rest require payment, an application, or both.`
      faqs.push({
        question: `Are there free ${subject}?`,
        answerPlain: freePlain,
        answer: topFreeCommunities.length ? (
          <>
            {free.count} of {total} ({free.percent}%) are marked as free to join,
            including {joinLinkedCommunities(topFreeCommunities)}. The rest
            require payment, an application, or both.
          </>
        ) : (
          freePlain
        ),
      })
    }
  }

  if (topPaidCommunities.length && restricted && restricted.count > 0) {
    const paidPlain = `${restricted.count} of ${total} (${restricted.percent}%) require payment, an application, or both. Notable paid listings include ${joinPlainCommunityNames(
      topPaidCommunities
    )}.`
    faqs.push({
      question: `Are there paid ${subject}?`,
      answerPlain: paidPlain,
      answer: (
        <>
          {restricted.count} of {total} ({restricted.percent}%) require payment,
          an application, or both. Notable paid listings include{" "}
          {joinLinkedCommunities(topPaidCommunities)}.
        </>
      ),
    })
  }

  const topFeatures = stats.commonFeatures.slice(0, 3)
  if (topFeatures.length) {
    const featureNames = topFeatures.map(f => f.name.toLowerCase())
    const linkFeatures = !isPlatformPage && templateFeatureSlugs.length > 0
    const plainSingle = `${topFeatures[0].name} is the most common feature (${topFeatures[0].percent}% of listings).`
    const plainMultiple = `${topFeatures[0].name} is the most common (${
      topFeatures[0].percent
    }%). Others that show up often include ${joinNames(
      featureNames.slice(1)
    )}. Use feature filters on ${siteName} to narrow results.`
    faqs.push({
      question: isPlatformPage
        ? `What features do these ${platformLabel} include?`
        : `What features do these communities offer?`,
      ...(linkFeatures && topFeatures.length === 1
        ? { answerPlain: plainSingle }
        : linkFeatures
        ? { answerPlain: plainMultiple }
        : {}),
      answer: linkFeatures ? (
        topFeatures.length === 1 ? (
          <>
            {linkedFeatureName(
              topFeatures[0],
              topic.slug,
              templateFeatureSlugs
            )}{" "}
            is the most common feature ({topFeatures[0].percent}% of listings).
          </>
        ) : (
          <>
            {linkedFeatureName(
              topFeatures[0],
              topic.slug,
              templateFeatureSlugs
            )}{" "}
            is the most common ({topFeatures[0].percent}%). Others that show up
            often include{" "}
            {joinLinkedFeatureNames(
              topFeatures.slice(1),
              topic.slug,
              templateFeatureSlugs
            )}
            . Use feature filters on {siteName} to narrow results.
          </>
        )
      ) : topFeatures.length === 1 ? (
        plainSingle
      ) : (
        plainMultiple
      ),
    })
  }

  const withMembers = communities.filter(c => toMembers(c) != null)
  const largestCommunities = withMembers.length
    ? sortCommunities("Largest", [...withMembers]).slice(0, 3)
    : []
  const sizePlain = `${siteName} lists ${total} ${subject} in ${directory}.${memberCountCopy(
    stats,
    communities
  )}${sampleNote}`
  faqs.push({
    question: `How large are ${subject}?`,
    answerPlain: sizePlain,
    answer:
      largestCommunities.length > 0 ? (
        <>
          {siteName} lists {total} {subject} in {directory}. The largest include{" "}
          {largestCommunities.map((c, index) => (
            <React.Fragment key={c.slug}>
              {index > 0 &&
                (index === largestCommunities.length - 1 ? ", and " : ", ")}
              <Link
                to={`/communities/${c.slug}/`}
                className={communityLinkClass}
              >
                {c.name}
              </Link>
              {` (${readableNumber(toMembers(c))} members)`}
            </React.Fragment>
          ))}
          .
          {stats.medianMembers != null ? (
            <>
              {" "}
              Median reported size is about{" "}
              {readableNumber(Math.round(stats.medianMembers))} members (
              {withMembers.length} listings with member data).
            </>
          ) : null}
          {sampleNote}
        </>
      ) : (
        sizePlain
      ),
  })

  if (isPlatformPage) {
    const answerPlain = `Browse listings in ${directory}, open a community that fits your needs, and follow its join link to ${platformName}. Some communities may require an application or paid membership, which is shown on the listing when known.`
    faqs.push({
      question: `How do I join a ${topicName} community on ${platformName}?`,
      answerPlain,
      answer: (
        <>
          Browse listings in {directory}, open a community that fits your
          needs, and follow its join link to {platformName}. Some communities
          may require an application or paid membership, which is shown on the
          listing when known.
        </>
      ),
    })
  }

  const growthQuestion = isPlatformPage
    ? `How do I grow my ${topicName} community on ${platformName}?`
    : `How do I grow my ${topicName} community?`
  const growthSubject = isPlatformPage
    ? `${topicName} ${platformLabel}`
    : `${topicName} communities`
  const growthAnswer = `List your ${topicName} community${
    isPlatformPage ? ` on ${platformName}` : ""
  } on ${siteName} for free to reach people already searching for ${growthSubject}. Paid promotion can feature your community more prominently across relevant pages and send more qualified visitors to your listing.`
  faqs.push({
    question: growthQuestion,
    answerPlain: growthAnswer,
    answer: (
      <>
        <Link to="/submit/" className="font-semibold underline">
          List your {topicName} community
          {isPlatformPage ? ` on ${platformName}` : ""} for free
        </Link>{" "}
        on {siteName} to reach people already searching for {growthSubject}.{" "}
        <Link to="/partners/community/" className="font-semibold underline">
          Paid promotion
        </Link>{" "}
        can feature your community more prominently across relevant pages and
        send more qualified visitors to your listing.
      </>
    ),
  })

  const freshness = updatedDate
    ? ` These listings in ${directory} were last updated ${updatedDate}.`
    : ""
  faqs.push({
    question: `How does ${siteName} collect and rank listings?`,
    answer: `Listings are submitted by community builders and researchers, reviewed for topic relevance, and updated with platform, feature, member, review, and traffic data where available. Member counts are approximate. Popular reflects visitor interest in ${directory}, with richer profiles breaking ties. Ratings, Largest, and Newest use review scores, reported member counts, and listing age instead.${
      hasPromoted
        ? " From time to time, paid ads may appear in promoted placements across the directory."
        : ""
    }${freshness}`,
  })

  return faqs.slice(0, 9)
}

export { buildTopicPageFAQ }
